import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import apiService from '@/services/api';

interface Order {
  id: number;
  service_id: number;
  service_name?: string;
  property_name?: string;
  property_id?: number;
  total_amount_pence: number;
  status: string;
  booking_status?: string;
  provider_id?: number;
  provider_name?: string;
  created_at: string;
  preferred_service_date?: string;
  description?: string;
}

const ActiveOrdersPage = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch active orders from API
  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await apiService.request('/customer/orders', 'GET');
        if (response.success && Array.isArray(response.data)) {
          // Filter to show only active/in-progress orders
          const activeOrders = response.data.filter((order: Order) => 
            order.status !== 'completed' && order.status !== 'cancelled'
          );
          setOrders(activeOrders.slice(0, 10)); // Show latest 10 active orders
        }
      } catch (error) {
        console.error('Failed to fetch active orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const ProgressBar = ({ currentStage }: { currentStage: number }) => {
    return (
      <div className="flex items-center gap-2 mt-4">
        {[1, 2, 3, 4].map((stage) => (
          <div key={stage} className="flex-1 relative">
            <div
              className={`h-1 rounded-full transition-colors ${
                stage <= currentStage ? 'bg-yellow-400' : 'bg-gray-300'
              }`}
            />
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-accent" />
        </div>
      </DashboardLayout>
    );
  }

  if (orders.length === 0) {
    return (
      <DashboardLayout>
        <div className="w-full text-center py-12">
          <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No Active Orders</h2>
          <p className="text-gray-600 mb-6">You don't have any active orders at the moment.</p>
          <Button onClick={() => navigate('/dashboard/request-service')} className="bg-accent hover:bg-accent/90">
            Request a Service
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Active Orders | Virewire Property Services</title>
        <meta name="description" content="Track your active service orders" />
      </Helmet>

      <DashboardLayout>
        <div className="w-full">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Active Orders
            </h1>
            <p className="text-gray-600">
              We are actively managing your service requests. Here you can track
              real-time progress and finalize pending approvals.
            </p>
          </div>

          {/* Orders Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {orders.map((order) => {
              const amountGBP = ((order.total_amount_pence ?? 0) / 100).toFixed(2);
              const status = order.booking_status || order.status || 'pending';
              // Show approve button only if status is pending/awaiting_approval AND no provider is assigned yet
              const isActionRequired = (status === 'pending' || status === 'awaiting_approval') && !order.provider_id;
              const currentStage = status === 'completed' ? 4 : status === 'in_progress' ? 3 : status === 'confirmed' ? 2 : 1;
              const createdDate = new Date(order.created_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric'
              });

              return (
                <Card
                  key={order.id}
                  className="border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="p-6">
                    {/* Order Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                          #ORD-{order.id}
                        </p>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">
                          {order.service_name}
                        </h3>
                        <p className="text-sm text-gray-600">{order.property_name || 'Property location'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs font-bold text-gray-600 uppercase mb-1">
                          Amount
                        </p>
                        <p className="text-xl font-bold text-[#00543b]">
                          £{amountGBP}
                        </p>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="mb-4 flex gap-2 flex-wrap">
                      {isActionRequired ? (
                        <button className="bg-yellow-400 text-gray-900 text-xs font-bold px-3 py-2 rounded-full hover:bg-yellow-500 transition-colors flex items-center gap-1">
                          ⚡ ACTION REQUIRED
                        </button>
                      ) : (
                        <button className="bg-green-500 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1">
                          ✓ IN PROGRESS
                        </button>
                      )}
                      {order.provider_id && (
                        <button className="bg-blue-500 text-white text-xs font-bold px-3 py-2 rounded-full flex items-center gap-1">
                          👷 {order.provider_name || 'Provider Assigned'}
                        </button>
                      )}
                    </div>

                    {/* Progress Bar */}
                    <ProgressBar currentStage={currentStage} />

                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4 mt-4">
                      {order.description || 'Service request in progress'}
                    </p>

                    {/* Date */}
                    <p className="text-xs text-gray-500 mb-4">
                      Created: {createdDate}
                    </p>

                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-4">
                      {isActionRequired ? (
                        <>
                          <Button
                            variant="ghost"
                            className="flex-1 border border-gray-300 text-gray-900 hover:bg-gray-50"
                            onClick={() => navigate(`/order/${order.id}#service-details`)}
                          >
                            View Details
                          </Button>
                          <Button
                            className="flex-1 bg-[#00543b] hover:bg-[#003d2d] text-white"
                            onClick={() => navigate(`/order/${order.id}`)}
                          >
                            Review & Approve
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="ghost"
                          className="flex-1 border border-gray-300 text-gray-900 hover:bg-gray-50"
                          onClick={() => navigate(`/order/${order.id}#timeline`)}
                        >
                          View Live Status
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default ActiveOrdersPage;
